import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { FileText, Search, Filter, AlertCircle, X, SortAsc, Check, ChevronDown, Grid, List, Sparkles, Loader2, FolderOpen } from 'lucide-react';
import GlobalHeader from '@/components/GlobalHeader';
import PrintButton from '@/components/PrintButton';
import SyncStatusIndicator from '@/components/SyncStatusIndicator';
import { getSyncStatus } from '@/services/offlineDataService';
import { toast } from 'sonner';
import API from '@/utils/api';
import { cn } from '@/lib/utils';
import APP_ICONS from '@/utils/appIcons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import DocumentsTabs from '@/components/documents/DocumentsTabs';
import IDsTravelTab from '@/components/documents/IDsTravelTab';
import InsuranceTab from '@/components/documents/InsuranceTab';
import CarRegistrationTab from '@/components/documents/CarRegistrationTab';
import LicensesCertsTab from '@/components/documents/LicensesCertsTab';
import HomeUtilitiesTab from '@/components/documents/HomeUtilitiesTab';
import WorkSchoolTab from '@/components/documents/WorkSchoolTab';
import OtherTab from '@/components/documents/OtherTab';
import DocumentCard from '@/components/documents/DocumentCard';
import BatchScanDialog from '@/components/home/BatchScanDialog';

// Documents module theme tokens
const DOCS_THEME = {
  bgGradient: 'linear-gradient(135deg, #FAFAF9 0%, #F5F4F2 50%, #F8F7F6 100%)',
  headerIcon: '#A8A5A1',
  headerIconBg: '#E8E7E5',
};

// Tab display names for print
const TAB_NAMES = {
  all: 'All Documents',
  ids_travel: 'IDs & Travel',
  insurance: 'Insurance',
  car_registration: 'Car Registration',
  licenses_certs: 'Licenses & Certifications',
  home_utilities: 'Home & Utilities',
  work_school: 'Work & School',
  other: 'Other Documents'
};

// Document type categories
const CATEGORY_FILTERS = [
  { value: 'passport', label: 'Passport' },
  { value: 'license', label: 'License' },
  { value: 'id_card', label: 'ID Card' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'registration', label: 'Registration' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'other', label: 'Other' },
];

// Sort options
const SORT_OPTIONS = [
  { value: 'updated_desc', label: 'Last Updated' },
  { value: 'title_asc', label: 'Name (A-Z)' },
  { value: 'expiry_asc', label: 'Expiring Soon' },
];

const DocumentsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDemoMode, getDocuments } = useDemo();
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('ids_travel');
  
  // Advanced filter states
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [sortBy, setSortBy] = useState('updated_desc');
  const [searchOCRContent, setSearchOCRContent] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [contentSearchResults, setContentSearchResults] = useState(null);
  const [showBatchScan, setShowBatchScan] = useState(false);
  
  // All documents for filtered view
  const [allDocuments, setAllDocuments] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [needsAttentionCount, setNeedsAttentionCount] = useState(0);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // Track pending sync changes
  useEffect(() => {
    const checkPendingChanges = () => {
      const status = getSyncStatus();
      setHasPendingChanges(status.hasPendingChanges);
    };
    
    checkPendingChanges();
    const interval = setInterval(checkPendingChanges, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch all documents when switching to "all" view or filters active
  useEffect(() => {
    if (activeTab === 'all' || showFilters || showExpiringOnly || selectedCategories.length > 0) {
      fetchAllDocuments();
    }
  }, [activeTab, refreshKey]);

  const fetchAllDocuments = async () => {
    setLoadingAll(true);
    try {
      if (isDemoMode) {
        const allDocs = getDocuments() || [];
        setAllDocuments(allDocs);
        
        // Count documents needing attention (expiring in 30 days or expired)
        const today = new Date();
        const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
        const needsAttention = allDocs.filter(doc => {
          if (!doc.expiry_date) return false;
          const expiry = new Date(doc.expiry_date);
          return expiry <= thirtyDaysFromNow;
        });
        setNeedsAttentionCount(needsAttention.length);
        setLoadingAll(false);
        return;
      }
      const response = await API.get('/documents');
      setAllDocuments(response.data);
      
      // Count documents needing attention (expiring in 30 days or expired)
      const today = new Date();
      const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      const needsAttention = response.data.filter(doc => {
        if (!doc.expiry_date) return false;
        const expiry = new Date(doc.expiry_date);
        return expiry <= thirtyDaysFromNow;
      });
      setNeedsAttentionCount(needsAttention.length);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoadingAll(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    setRefreshKey(prev => prev + 1);
    setContentSearchResults(null);
    toast.success('Data refreshed');
  }, []);

  // Perform OCR content search
  const performContentSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setContentSearchResults(null);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await API.get('/documents/search/content', {
        params: { q: query, limit: 50 }
      });
      setContentSearchResults(response.data);
    } catch (error) {
      console.error('Content search failed:', error);
      toast.error('Search failed');
      setContentSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced content search
  useEffect(() => {
    if (!searchOCRContent || !searchQuery) {
      setContentSearchResults(null);
      return;
    }
    
    const timer = setTimeout(() => {
      performContentSearch(searchQuery);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery, searchOCRContent, performContentSearch]);

  // Filter and sort documents
  const getFilteredDocuments = () => {
    let filtered = [...allDocuments];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.title?.toLowerCase().includes(query) ||
        doc.issuer?.toLowerCase().includes(query) ||
        doc.document_number?.toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(doc => 
        selectedCategories.includes(doc.document_type) ||
        selectedCategories.includes(doc.category)
      );
    }
    
    // Expiring filter
    if (showExpiringOnly) {
      const today = new Date();
      const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(doc => {
        if (!doc.expiry_date) return false;
        const expiry = new Date(doc.expiry_date);
        return expiry <= thirtyDaysFromNow;
      });
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title_asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'expiry_asc':
          if (!a.expiry_date && !b.expiry_date) return 0;
          if (!a.expiry_date) return 1;
          if (!b.expiry_date) return -1;
          return new Date(a.expiry_date) - new Date(b.expiry_date);
        case 'updated_desc':
        default:
          return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      }
    });
    
    return filtered;
  };

  const handleCategoryToggle = (category) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategories([]);
    setShowExpiringOnly(false);
    setSortBy('updated_desc');
    setContentSearchResults(null);
    setSearchOCRContent(false);
  };

  const activeFilterCount = selectedCategories.length + (showExpiringOnly ? 1 : 0);

  // Render content search results
  const renderContentSearchResults = () => {
    if (!contentSearchResults) return null;
    
    const { query, total, results } = contentSearchResults;
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <p className="text-sm text-slate-600">
              Found <span className="font-semibold text-slate-900">{total}</span> document{total !== 1 ? 's' : ''} containing "{query}"
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchQuery('');
              setContentSearchResults(null);
            }}
          >
            Clear search
          </Button>
        </div>
        
        {results.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
            <Search className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No documents found with that content</p>
            <p className="text-sm text-slate-400 mt-1">Try different keywords or disable AI Search for basic search</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.map(doc => (
              <div key={doc.id} className="relative">
                <DocumentCard 
                  document={doc} 
                  onDelete={() => {
                    fetchAllDocuments();
                    performContentSearch(searchQuery);
                  }}
                />
                {/* Show matching snippets */}
                {doc._snippets && doc._snippets.length > 0 && (
                  <div className="mt-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-xs text-amber-700 font-medium mb-1">Found in:</p>
                    {doc._snippets.slice(0, 2).map((snippet, idx) => (
                      <p key={idx} className="text-xs text-slate-600 truncate">
                        <span className="text-amber-600">{snippet.field}:</span> {snippet.value}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render the active tab content
  const renderTabContent = () => {
    // If content search is active and has results, show them
    if (searchOCRContent && contentSearchResults) {
      return renderContentSearchResults();
    }
    
    // If filters are active, show filtered "All Documents" view
    if (activeFilterCount > 0 || activeTab === 'all') {
      const filteredDocs = getFilteredDocuments();
      
      if (loadingAll) {
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        );
      }
      
      if (filteredDocs.length === 0) {
        return (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No documents match your filters</p>
            <Button variant="link" onClick={clearFilters} className="mt-2">
              Clear filters
            </Button>
          </div>
        );
      }
      
      return (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Showing {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredDocs.map(doc => (
              <DocumentCard 
                key={doc.id} 
                document={doc} 
                onDelete={() => {
                  fetchAllDocuments();
                  handleRefresh();
                }}
              />
            ))}
          </div>
        </div>
      );
    }
    
    // Normal tab view
    const key = `${activeTab}-${refreshKey}`;
    switch (activeTab) {
      case 'ids_travel':
        return <IDsTravelTab key={key} searchQuery={searchQuery} />;
      case 'insurance':
        return <InsuranceTab key={key} searchQuery={searchQuery} />;
      case 'car_registration':
        return <CarRegistrationTab key={key} searchQuery={searchQuery} />;
      case 'licenses_certs':
        return <LicensesCertsTab key={key} searchQuery={searchQuery} />;
      case 'home_utilities':
        return <HomeUtilitiesTab key={key} searchQuery={searchQuery} />;
      case 'work_school':
        return <WorkSchoolTab key={key} searchQuery={searchQuery} />;
      case 'other':
        return <OtherTab key={key} searchQuery={searchQuery} />;
      default:
        return <IDsTravelTab key={key} searchQuery={searchQuery} />;
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{ background: DOCS_THEME.bgGradient }}
      data-testid="documents-page"
    >
      <GlobalHeader
        iconSrc={APP_ICONS.documents}
        iconBgColor={DOCS_THEME.headerIconBg}
        iconColor="text-stone-600"
        title="Documents"
        subtitle="Secure storage for important files"
        onRefresh={handleRefresh}
      >
        <PrintButton title={`Documents - ${TAB_NAMES[activeTab]}`} />
      </GlobalHeader>

      {/* Sync Status Banner - shows when there are pending changes */}
      {hasPendingChanges && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4">
          <SyncStatusIndicator variant="full" size="md" />
        </div>
      )}

      {/* Print Header - Only visible when printing */}
      <div className="print-only print-header px-6 py-4">
        <div>
          <h1 className="print-title">Documents</h1>
          <p className="text-sm text-slate-600">{TAB_NAMES[activeTab]}</p>
        </div>
        <div className="print-date">
          Printed: {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Search and Filter Bar */}
        <div className="mb-4 space-y-3 no-print">
          <div className="flex gap-2">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder={searchOCRContent ? "Search document content..." : "Search by document name..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-slate-200"
                data-testid="document-search-input"
              />
              {isSearching && (
                <Loader2 className="absolute right-10 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
              )}
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setContentSearchResults(null);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* OCR Content Search Toggle */}
            <Button
              variant={searchOCRContent ? "default" : "outline"}
              onClick={() => setSearchOCRContent(!searchOCRContent)}
              className={cn(
                "gap-2 whitespace-nowrap",
                searchOCRContent && "bg-amber-500 hover:bg-amber-600"
              )}
              data-testid="ocr-search-toggle"
              title="Search inside document content using AI-extracted text"
            >
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">AI Search</span>
            </Button>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "gap-2",
                activeFilterCount > 0 && "border-purple-300 bg-purple-50"
              )}
              data-testid="filter-toggle"
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <Badge className="bg-purple-500 text-white text-xs px-1.5">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>

            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="sort-dropdown">
                  <SortAsc className="h-4 w-4" />
                  <span className="hidden sm:inline">Sort</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {SORT_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setSortBy(option.value)}
                    className="gap-2"
                  >
                    {sortBy === option.value && <Check className="h-4 w-4" />}
                    {sortBy !== option.value && <div className="w-4" />}
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Batch Scan Button */}
            <Button
              onClick={() => setShowBatchScan(true)}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
              data-testid="batch-scan-btn"
            >
              <FolderOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Batch Scan</span>
            </Button>
          </div>

          {/* Expanded Filter Panel */}
          {showFilters && (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
              {/* Needs Attention Quick Filter */}
              <div className="flex items-center justify-between">
                <Button
                  variant={showExpiringOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowExpiringOnly(!showExpiringOnly)}
                  className={cn(
                    "gap-2",
                    showExpiringOnly && "bg-red-500 hover:bg-red-600"
                  )}
                  data-testid="expiring-filter"
                >
                  <AlertCircle className="h-4 w-4" />
                  Needs Attention
                  {needsAttentionCount > 0 && (
                    <Badge className={cn(
                      "text-xs px-1.5",
                      showExpiringOnly ? "bg-white text-red-600" : "bg-red-100 text-red-600"
                    )}>
                      {needsAttentionCount}
                    </Badge>
                  )}
                </Button>

                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear all
                  </Button>
                )}
              </div>

              {/* Category Filters */}
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                  Filter by Category
                </p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_FILTERS.map((category) => {
                    const isSelected = selectedCategories.includes(category.value);
                    return (
                      <Button
                        key={category.value}
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleCategoryToggle(category.value)}
                        className={cn(
                          "text-xs",
                          isSelected && "bg-purple-500 hover:bg-purple-600"
                        )}
                        data-testid={`category-filter-${category.value}`}
                      >
                        {isSelected && <Check className="h-3 w-3 mr-1" />}
                        {category.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {(selectedCategories.length > 0 || showExpiringOnly) && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-slate-500">Active filters:</span>
              
              {showExpiringOnly && (
                <Badge variant="secondary" className="gap-1 bg-red-100 text-red-700">
                  Needs Attention
                  <button onClick={() => setShowExpiringOnly(false)}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              
              {selectedCategories.map((cat) => {
                const catLabel = CATEGORY_FILTERS.find(c => c.value === cat)?.label || cat;
                return (
                  <Badge key={cat} variant="secondary" className="gap-1 bg-purple-100 text-purple-700">
                    {catLabel}
                    <button onClick={() => handleCategoryToggle(cat)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}
        </div>

        {/* Content Card */}
        <Card 
          className="shadow-sm border overflow-hidden print-card"
          style={{ 
            backgroundColor: '#FAFAF9',
            borderColor: '#E5E4E2',
            borderRadius: '1rem'
          }}
        >
          <CardContent className="p-4 sm:p-6">
            {activeFilterCount > 0 ? (
              // Show filtered results
              renderTabContent()
            ) : (
              // Show normal tabs
              <DocumentsTabs activeTab={activeTab} onTabChange={setActiveTab}>
                {renderTabContent()}
              </DocumentsTabs>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Batch Scan Dialog */}
      <BatchScanDialog 
        open={showBatchScan} 
        onOpenChange={setShowBatchScan} 
      />
    </div>
  );
};

export default DocumentsPage;
